import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import LinearProgress from "@material-ui/core/LinearProgress";
import ListItem from "@material-ui/core/ListItem";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";
import { WithStyles, withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Delete from "@material-ui/icons/Delete";
import Edit from "@material-ui/icons/Edit";
import * as React from "react";
import Component from "src/shared/components/Component";
import PartialCleanCheck from "src/shared/components/PartialCleanCheck";
import withAxios, { IAxiosProps } from "src/shared/components/withAxios";
import { ISport, ISportData } from "src/api/types";

const styles = () => ({
  listItem: {
    "&:hover": {
      backgroundColor: "#e6e6e6",
    },
    backgroundColor: "white",
  },
});

interface ISportPageProps {
  sport: ISport;
  openDeleteModal: (deleteSport: ISport) => () => void;
  loadSports: () => void;
  setSport: (sport: ISport, sportName: string) => void;
}

interface ISportsPageState {
  editing?: true;
  submitting?: true;
  sportName: string;
}

type IProps = ISportPageProps &
  IAxiosProps &
  WithStyles<keyof ReturnType<typeof styles>>;

export default withStyles(styles)(
  withAxios(
    class extends Component<IProps, ISportsPageState> {
      public readonly state = this.buildState({
        sportName: "",
      });

      private textInput = React.createRef<HTMLInputElement>();

      private readonly handleSportNameChange = this.stateInputEventHandler(
        (v) => {
          return { sportName: v };
        }
      );

      private partialCleanCheck: PartialCleanCheck<ISportsPageState> =
        new PartialCleanCheck(this, ["sportName"], (state) => {
          return {
            ...state,
            sportName:
              state.sportName !== undefined ? state.sportName.trim() : "",
          };
        });

      public componentDidMount() {
        document.addEventListener("keydown", this.escFunction, false);
      }
      
      public componentWillUnmount() {
        document.removeEventListener("keydown", this.escFunction, false);
      }

      public render() {
        const { sport } = this.props;
        const { editing } = this.state;
        return (
          <ListItem
            divider={true}
            key={sport.id}
            className={this.props.classes.listItem}
            style={editing ? { backgroundColor: "#f0fbff" } : undefined}
          >
            {editing !== undefined
              ? this.renderEditing(sport)
              : this.renderNonEditing(sport)}
          </ListItem>
        );
      }

      private renderNonEditing(sport: ISport) {
        const { openDeleteModal } = this.props;
        return (
          <React.Fragment>
            <ListItemText
              onClick={this.startEditing}
              primary={`${sport.name}`}
            />
            <ListItemSecondaryAction>
              <Tooltip title="Edit" placement="left">
                <IconButton aria-label="Edit" onClick={this.startEditing}>
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" placement="left">
                <IconButton
                  aria-label="Delete"
                  onClick={openDeleteModal(sport)}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </React.Fragment>
        );
      }

      private renderEditing(sport: ISport) {
        const { sportName, submitting } = this.state;
        return (
          <React.Fragment>
            <form onSubmit={this.updateSport}>
              <Grid
                container={true}
                direction="row"
                spacing={2}
                alignItems="center"
              >
                <Grid item={true}>
                  <TextField
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    inputRef={this.textInput}
                    label={sport.name}
                    value={sportName}
                    onChange={this.handleSportNameChange}
                  />
                </Grid>
                <Grid>
                  <Button
                    type="submit"
                    disabled={!this.canSubmit()}
                    variant="contained"
                    color="primary"
                  >
                    Submit
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={this.stopEditing}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
              {submitting !== undefined && <LinearProgress />}
            </form>
          </React.Fragment>
        );
      }

      private onFocus = () => {
        document.addEventListener("keydown", this.escFunction, false);
      };

      private onBlur = () => {
        // this.setState({focused: undefined});
        document.removeEventListener("keydown", this.escFunction, false);
      };

      private escFunction = (event: KeyboardEvent) => {
        if (event.keyCode === 27) {
          this.setState({ editing: undefined });
        }
      };

      private focus = () => {
        const node = this.textInput.current;
        if (node) {
          node.focus();
        }
      };

      private canSubmit = () => {
        const { submitting, sportName } = this.state;
        return (
          Boolean(sportName.trim()) &&
          submitting === undefined &&
          !this.partialCleanCheck.isClean()
        );
      };

      private startEditing = () => {
        if (this.state.editing === undefined) {
          this.setState(
            (prevState) => {
              const newState: { editing?: true; sportName: string } = {
                editing: true,
                sportName: this.props.sport.name,
              };
              this.partialCleanCheck.setOldFormData(newState);
              return newState;
            },
            () => {
              this.focus();
            }
          );
        }
      };

      private stopEditing = () => {
        if (this.state.editing) {
          this.setState({ editing: undefined });
        }
      };

      private updateSport = async (e: React.FormEvent) => {
        e.preventDefault();
        this.runCancellableStatefulAsync(
          this,
          {
            submitting: true,
          },
          {
            editing: undefined,
            submitting: undefined,
          },
          async () => {
            const { sport, setSport } = this.props;
            const { sportName } = this.state;
            const sportEdit = {
              id: sport.id,
              name: sportName.trim(),
            };
            await this.buildPut<ISportData, void>(this, "sport")(sportEdit);
            setSport(sport, sportEdit.name);
            this.props.loadSports();
          },
          () => {
            window.alert("Failed to update sport");
            this.setState({ submitting: undefined });
          }
        );
      };
    }
  )
);



// WEBPACK FOOTER //
// ./src/features/admin/pages/sportsPage/SportListItem.tsx